import React, { useCallback, useState, useEffect } from "react";
import { AppExtensionSDK } from "@contentful/app-sdk";
import {
  Heading,
  Form,
  TextInput,
  FormControl,
  Notification,
  Box,
  Paragraph,
  Flex,
} from "@contentful/f36-components";
import tokens from "@contentful/f36-tokens";
import { css } from "emotion";
import { useCMA, useSDK } from "@contentful/react-apps-toolkit";

const rawData = (name: string) => ({
  name,
  displayField: "title",
  fields: [
    {
      id: "title",
      name: "Title",
      type: "Symbol",
      localized: false,
      required: true,
      validations: [],
      disabled: false,
      omitted: false,
    },
    {
      id: "content",
      name: "Content",
      type: "Object",
      localized: true,
      required: false,
      validations: [],
      disabled: false,
      omitted: false,
    },
    {
      id: "contentReferences",
      name: "Content references",
      type: "Array",
      localized: false,
      required: false,
      validations: [],
      disabled: false,
      omitted: false,
      items: {
        type: "Link",
        validations: [],
        linkType: "Entry",
      },
    },
    {
      id: "contentMediaReferences",
      name: "Content media references",
      type: "Array",
      localized: false,
      required: false,
      validations: [],
      disabled: false,
      omitted: false,
      items: {
        type: "Link",
        validations: [],
        linkType: "Asset",
      },
    },
  ],
});

export interface AppInstallationParameters {
  editorUrl: string;
  accessToken: string;
}

const ConfigScreen = () => {
  const [parameters, setParameters] = useState<AppInstallationParameters>({
    editorUrl: "",
    accessToken: "",
  });

  // @ts-ignore
  const sdk = useSDK<AppExtensionSDK>();
  const cma = useCMA();
  const {
    ids: { environment, space, app },
  } = useSDK();

  const onConfigure = useCallback(async () => {
    // This method will be called when a user clicks on "Install"
    // or "Save" in the configuration screen.
    // for more details see https://www.contentful.com/developers/docs/extensibility/ui-extensions/sdk-reference/#register-an-app-configuration-hook

    // Get current the state of EditorInterface and other entities
    // related to this app installation
    const currentState = await sdk.app.getCurrentState();

    try {
      await createContentModel({
        contentTypeId: "shopstoryBlock",
        name: "Shopstory Block",
        environment,
        space,
      });
      // await createContentModel({
      //   contentTypeId: "shopstoryGrid",
      //   name: "Shopstory Grid",
      //   environment,
      //   space,
      // });
    } catch (e: any) {
      console.error("shopstoryBlock", e);
      if ("message" in e) {
        Notification.error(JSON.parse(e.message).message ?? "Unknown Error");
      }
    }

    async function getOrCreate({
      contentTypeId,
      environment,
      space,
      name,
    }: {
      contentTypeId: string;
      name: string;
      environment: string;
      space: string;
    }) {
      const params = { contentTypeId, environment, space };

      try {
        return await cma.contentType.get(params);
      } catch (e: any) {
        if (e?.code === "NotFound") {
          return await cma.contentType.createWithId(params, rawData(name));
        }
        throw e;
      }
    }

    async function createContentModel({
      contentTypeId,
      environment,
      space,
      name,
    }: {
      contentTypeId: string;
      name: string;
      environment: string;
      space: string;
    }) {
      const contentModel = await getOrCreate({
        contentTypeId,
        environment,
        space,
        name,
      });

      if (!contentModel) {
        const message = `Could not create content type ${name}`;
        console.warn(message);
        Notification.warning(message);
        return;
      }

      await cma.contentType.publish(
        { contentTypeId, environment, space },
        contentModel
      );
    }

    return {
      parameters,
      targetState: {
        ...currentState,
        EditorInterface: {
          ...currentState?.EditorInterface,
          shopstoryBlock: {
            controls: [
              {
                fieldId: "content",
                settings: {
                  mode: "content",
                },
              },
            ],
          },
          // shopstoryGrid: {
          //   controls: [
          //     {
          //       fieldId: "config",
          //       settings: {
          //         mode: "grid",
          //       },
          //     },
          //   ],
          // },
        },
      },
    };
  }, [parameters, sdk, environment, space, cma]);

  useEffect(() => {
    // `onConfigure` allows to configure a callback to be
    // invoked when a user attempts to install the app or update
    // its configuration.
    sdk.app.onConfigure(() => onConfigure());
  }, [sdk, onConfigure]);

  useEffect(() => {
    (async () => {
      // Get current parameters of the app.
      // If the app is not installed yet, `parameters` will be `null`.
      const currentParameters: AppInstallationParameters | null =
        await sdk.app.getParameters();

      if (currentParameters) {
        setParameters(currentParameters);
      }

      // Once preparation has finished, call `setReady` to hide
      // the loading screen and present the app to a user.
      sdk.app.setReady();
    })();
  }, [sdk]);

  const setEditorUrl = (editorUrl: string) => {
    setParameters({
      ...parameters,
      editorUrl,
    });
  };

  const setAccessToken = (accessToken: string) => {
    setParameters({
      ...parameters,
      accessToken,
    });
  };

  return (
    <>
      <div
        className={css({
          display: "block",
          position: "absolute",
          zIndex: tokens.zIndexNegative,
          top: "0",
          width: "100%",
          height: "300px",
          backgroundColor: "#F0FB98",
        })}
      />
      <Box
        paddingTop="spacingXl"
        paddingRight="spacing2Xl"
        paddingBottom="spacingXl"
        paddingLeft="spacing2Xl"
        marginTop="spacingXl"
        className={css({
          maxWidth: tokens.contentWidthText,
          marginInline: "auto",
          backgroundColor: tokens.colorWhite,
          boxShadow: tokens.boxShadowDefault,
          borderRadius: tokens.borderRadiusSmall,
          zIndex: tokens.zIndexDefault,
          minHeight: "50vh",
        })}
      >
        <Heading>Shopstory</Heading>
        <Paragraph>The first visual builder for Contentful.</Paragraph>
        <hr
          className={css({
            marginTop: tokens.spacingL,
            marginBottom: tokens.spacingL,
            border: 0,
            height: "1px",
            backgroundColor: tokens.gray300,
          })}
        />
        <Heading>Configuration</Heading>
        <Form>
          <FormControl isRequired isInvalid={!parameters.editorUrl}>
            <FormControl.Label>Canvas URL</FormControl.Label>
            <TextInput
              value={parameters.editorUrl}
              type="text"
              name="domain"
              placeholder="http://www.example.com/shopstory-editor"
              onChange={(e) => setEditorUrl(e.target.value)}
            />
            {!parameters.editorUrl && (
              <FormControl.ValidationMessage>
                Please, provide the URL
              </FormControl.ValidationMessage>
            )}
          </FormControl>

          <FormControl>
            <FormControl.Label>Access token</FormControl.Label>
            <TextInput
              value={parameters.accessToken}
              type="text"
              name="domain"
              placeholder="Shopstory access token"
              onChange={(e) => setAccessToken(e.target.value)}
            />
          </FormControl>
        </Form>
      </Box>
      <Flex
        justifyContent={"center"}
        marginTop={"spacingXl"}
        marginBottom={"spacingXl"}
      >
        <svg
          viewBox="0 0 40 40"
          width={80}
          height={80}
          style={{ background: "gray" }}
          fill="black"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect width={40} height={40} fill="#F0FB98" />
          <path
            d="M24.8625 24.3526C24.8625 22.0386 23.2513 20.4408 21.4253 19.0909L20.0559 18.0716C18.6595 17.0248 17.9076 16.2534 17.9076 15.0964C17.9076 13.7741 19.0354 13.0028 20.4318 13.0028C21.7207 13.0028 22.768 13.4986 23.5467 14.876L26.2857 13.1956C24.9162 10.7989 22.7948 10 20.5661 10C17.3437 10 14.739 11.9559 14.739 15.2066C14.739 17.438 16.0011 19.0083 18.0687 20.5234L19.4382 21.5427C20.942 22.5895 21.6133 23.3884 21.6133 24.6281C21.6133 26.0055 20.4049 26.9972 18.6058 26.9972C16.6724 26.9972 15.464 25.8678 14.7927 24.2424L12 25.9504C13.0741 28.292 15.3298 30 18.4178 30C22.4189 30 24.8625 27.5207 24.8625 24.3526Z"
            fill="black"
          />
        </svg>
      </Flex>
    </>
  );
};

export default ConfigScreen;
